import React, { useState } from 'react';
import Select, { ValueType } from 'react-select';
import classnames from 'classnames';

import { makeStyles, useTheme } from '@material-ui/core/styles';

import { Translate } from '@hydra/common/components';
import { sendEvent } from '@hydra/utils/gtm';

const useStyles = makeStyles(theme => ({
  mutedContent: {
    color: theme.palette.grey[500],
    fontSize: theme.typography.fontSizeSmall
  },
  nearLimit: {
    color: theme.palette.warning.medium
  },
  exceedLimit: {
    color: theme.palette.error.medium
  },
  textContainer: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  title: {
    fontSize: theme.typography.fontSizeLarge,
    fontWeight: theme.typography.fontWeightSemibold,
    marginBottom: theme.spacing(0.75)
  }
}));

export const normalizeKeywords = (keywordInputValue: string): string[] => {
  return keywordInputValue.replace(/(\r\n|\n|\r)/gm, ',').split(',')
    .map(keyword => keyword.trim().toLowerCase())
    .filter(keyword => keyword.length > 0);
};

interface SelectKeywordsProps {
  keywordsLeft: number
  selectKeywords: (keyword: string[]) => void
  unSelectKeywords: (keywords: string[]) => void
  selectedKeywords: string[]
  createOption: (label: string) => { label: string; value: string; }
  keywordsProvision: number
}

export default function SelectKeywords ({
  keywordsLeft,
  selectKeywords,
  unSelectKeywords,
  selectedKeywords,
  createOption,
  keywordsProvision
}: SelectKeywordsProps) {
  const [keywordInputValue, setKeywordInputValue] = useState('');
  const theme = useTheme();
  const classes = useStyles();

  const confirmSelectKeywords = () => {
    if (!keywordInputValue) return;
    sendEvent('keywordtool-addkeyword-keywordadded-label');
    selectKeywords(normalizeKeywords(keywordInputValue));
    setKeywordInputValue('');
  };

  const handleNewEntryKeywordInput = async (keywordInputValue: string, { action }: { action: string }) => {
    if (selectedKeywords.length >= keywordsLeft) {
      return;
    }
    if (action === 'input-blur') {
      confirmSelectKeywords();
    } else if (action === 'input-change') {
      setKeywordInputValue(keywordInputValue);
    } else if (action === 'paste-data') {
      if (normalizeKeywords(keywordInputValue).length > 1) {
        sendEvent('keywordtool-addkeyword-keywordadded-label');
        selectKeywords(normalizeKeywords(keywordInputValue));
        setKeywordInputValue('');
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();

    const clipboardData = e.clipboardData || (window as any).clipboardData;
    const pastedData = clipboardData.getData('Text');

    handleNewEntryKeywordInput(pastedData, { action: 'paste-data' });
  };

  const handleChange = (keywords: ValueType<{
    label: string;
    value: string;
  }, true>) => {
    if (keywords && keywords.length === 0) {
      sendEvent('keywordtool-addkeyword-keywordscleared-btn');
    } else {
      sendEvent('keywordtool-addkeyword-keywordremoved-label');
    }
    unSelectKeywords(keywords ? keywords.map(({ value }) => value) : []);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    switch (event.key) {
      case 'Enter':
      case 'Tab':
      case ',':
        event.preventDefault();
        confirmSelectKeywords();
    }
  };

  const addKeywordsSelectStyles = () => {
    return {
      valueContainer: (base: any) => ({
        ...base,
        minHeight: '100px',
        alignItems: 'flex-start',
        padding: '10px'
      }),
      multiValue: (base: any) => ({
        ...base,
        borderRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.primary.main
      }),
      multiValueLabel: (base: any) => ({
        ...base,
        color: theme.palette.common.white,
        fontSize: theme.typography.fontSizeSmall,
        fontWeight: theme.typography.fontWeightRegular
      }),
      multiValueRemove: (base: any) => ({
        ...base,
        color: theme.palette.common.white,
        cursor: 'pointer',
        '&:hover': {
          color: theme.palette.common.white,
          backgroundColor: theme.palette.primary.main
        }
      }),
      clearIndicator: (base: any) => ({
        ...base,
        cursor: 'pointer'
      })
    };
  };

  const provisionClasses = classnames(
    classes.mutedContent,
    keywordsProvision <= 0 ? classes.exceedLimit : null
  );

  return (
    <>
      <Translate content='keywords' component='div' className={classes.title} />
      <div onPaste={handlePaste}>
        <Select
          className='nr-type-keywords'
          autoFocus
          placeholder=''
          components={{ DropdownIndicator: null }}
          isClearable
          menuIsOpen={false}
          isMulti
          value={selectedKeywords.map(keyword => createOption(keyword))}
          inputValue={selectedKeywords.length >= keywordsLeft ? '' : keywordInputValue}
          onChange={handleChange}
          onInputChange={handleNewEntryKeywordInput}
          onKeyDown={handleKeyDown}
          styles={addKeywordsSelectStyles()}
        />
      </div>
      <div className={classes.textContainer}>
        <Translate
          className={classes.mutedContent}
          component='span'
          content='serp_press_enter_separate_comma'
        />
        <span className={provisionClasses}>
          {keywordsProvision >= 0
            ? (
              <>
                {keywordsProvision}
                <Translate content='keywords_left' />
              </>
              )
            : <Translate content='keywords_limit_reached' />}
        </span>
      </div>
    </>
  );
}
